/**
 * ScrollToTop — robust global route-change scroll reset.
 *
 * Resets the window scroll position to the top whenever the React Router
 * pathname (or search/hash) changes. Without this, clicking a link from the
 * bottom of a long page lands the user at the bottom of the new page
 * (browsers preserve scroll for client-side navigations), making the
 * navigation feel broken.
 *
 * Hardened to handle three real-world cases that the naive
 * ``useEffect(() => scrollTo(0))`` pattern misses:
 *
 * 1. **Lazy-loaded routes** — when the new route is in a ``<Suspense>``
 *    boundary, the pathname changes BEFORE the lazy chunk + page have
 *    mounted. A single immediate ``scrollTo(0)`` runs against the
 *    near-empty fallback; once the heavy page mounts a frame later, it
 *    can restore its own scroll. We schedule a second ``scrollTo`` after
 *    ``requestAnimationFrame`` to win that race.
 *
 * 2. **Query-param / tab changes** — many pages drive tabs via
 *    ``?tab=upcoming``. Depending on ``location.search`` (not just
 *    ``pathname``) means switching tabs also resets to the top.
 *
 * 3. **Anchor links (``#section``)** — if the new URL has a hash, we
 *    scroll to that element instead of the top, otherwise hash links
 *    would be silently broken by this component.
 *
 * Also disables the browser's native scroll restoration (set once at
 * mount). Browsers tend to "remember" the scroll position on back/forward
 * navigations, which fights with this component's resets.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ScrollToTop = () => {
  const { pathname, search, hash } = useLocation();

  // Disable browser's scroll restoration once at mount. Without this, the
  // browser will sometimes restore a stale scroll position on back/forward
  // *after* our scrollTo, undoing the reset.
  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      try {
        window.history.scrollRestoration = 'manual';
      } catch { /* Safari can throw in private mode */ }
    }
  }, []);

  useEffect(() => {
    // Anchor link in URL → scroll to that element instead of the top.
    // Defer one tick so the destination DOM element exists.
    if (hash) {
      requestAnimationFrame(() => {
        try {
          const target = document.querySelector(hash);
          if (target) {
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
          }
        } catch { /* invalid hash selector */ }
        // No matching element — fall through to top-scroll
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      });
      return undefined;
    }

    // Two-phase reset:
    //   Phase 1 (sync) — fires immediately so the fallback doesn't paint mid-page.
    //   Phase 2 (RAF) — fires AFTER the new lazy-loaded route renders, beating
    //                   any restoration the browser/page tries.
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    const raf = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    });
    return () => cancelAnimationFrame(raf);
  }, [pathname, search, hash]);

  return null;
};

export default ScrollToTop;
